import * as Sentry from '@sentry/react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { PaymentIntentResult, SetupIntentResult, StripeError } from '@stripe/stripe-js';
import { useIsMutating } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { getTableDisplayName, toLuxon, TDR } from 'tdr-common';
import {
	MUTATION_KEY as confirmFreeSessionKey,
	useConfirmFreeCheckoutSession
} from '../../../api/confirmFreeChecktouSession';
import { useTable } from '../../../api/getTable';
import { enableFastCheckout } from '../../../api/guest';
import { MUTATION_KEY as releaseSessionKey } from '../../../api/releaseCheckoutSession';
import { CheckoutCTALabel, getCheckoutCTALabel, useCheckoutContext } from '../../../context/new/CheckoutContext';
import { analytics } from '../../../helpers/analytics';
import { getCdnImageUri } from '../../../helpers/getCDNURI';
import { trackReservationCreation } from '../../../helpers/reservationEventTrackers';
import layoutStyles from '../../../layouts/CheckoutLayout.module.scss';
import { ConfirmationFailed } from '../../../pages/errors/ConfirmationFailed';
import { CheckoutGuardOutletContext } from '../../../routes/CheckoutGuard';
import { CTAButton } from '../../buttons';
import SecureEncryptInfo from '../../dialog/info-dialog/SecureEncryptInfo';
import { FilterIcons } from '../../filter-icons/FilterIcons';
import { InputValidationError } from '../../form-inputs/InputValidationError';
import { SubHeader } from '../../headers/SubHeader';
import {
	ImageCard,
	ImageCardAsset,
	ImageCardHeader,
	ImageCardHeaderPrimary,
	ImageCardHeaderTitle
} from '../../image-card/ImageCard';
import { AddOnOrderSummary } from '../AddOnOrderSummary';
import { ContactUsFooter } from '../ContactUsFooter';
import { OrderSummary } from '../OrderSummary';
import { PaymentInput } from '../PaymentInput';
import { PromoCodeInput } from '../PromoCodeInput';
import { ScrollDownButton } from '../ScrollDownButton';
import { CONFIRM_RESERVATION } from '../../../helpers/analytics/constants';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useGTMAnalytics } from '../../../helpers/gtm/surface/useGTMAnalytics';

export const Payment = () => {
	const { t, i18n } = useTranslation();
	const { restaurant, featureFlags, checkoutSession, invoice } = useOutletContext<CheckoutGuardOutletContext>();
	const { checkoutState, isSaveContactChecked } = useCheckoutContext();
	const gtm = useGTMAnalytics();

	const { mutateAsync: confirmFreeCheckoutSession } = useConfirmFreeCheckoutSession();

	const { data: table } = useTable(checkoutSession.tableId);
	const displayTableName = table ? getTableDisplayName(table) : undefined;

	const isReleasing = useIsMutating({
		mutationKey: [releaseSessionKey],
		exact: true
	});

	const hasTrackedInvoiceView = useRef(false);

	const isFreeBookingSubmitting = useIsMutating({
		mutationKey: [confirmFreeSessionKey],
		exact: true
	});

	const stripe = useStripe();
	const elements = useElements();
	const navigate = useNavigate();

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [stripeError, setStripeError] = useState('');
	const [isPaymentInfoComplete, setIsPaymentInfoComplete] = useState(false);

	const [freeBookingError, setFreeBookingError] = useState(false);

	const [isScrollButtonVisbile, setIsScrollButtonVisible] = useState(false);
	const scrollRef = useRef<HTMLDivElement>();

	const formatDateShort = (luxonDateTime?: DateTime, language?: string) => {
		if (!luxonDateTime) {
			return '';
		}
		const locale = language === 'fr' ? 'fr-CA' : 'en';
		return luxonDateTime
			.setLocale(locale)
			.toLocaleString({ month: 'short', day: 'numeric' });
	};

	const formatTime = (luxonDateTime?: DateTime) => {
		return luxonDateTime ? luxonDateTime.toFormat('h:mma') : '';
	};

	const luxonStartDateTime = toLuxon(checkoutSession.time, restaurant.timezone);
	const displayDate = formatDateShort(luxonStartDateTime, i18n.language);
	const displayTime = formatTime(luxonStartDateTime);
	const addOns = checkoutSession.items.filter((item) => item.type === 'addon');
	const requiresCreditCard = restaurant.allowNoShowFees || invoice.total > 0;
	const isValid = !stripeError && (requiresCreditCard ? isPaymentInfoComplete : true);

	const imageSrc = getCdnImageUri(table?.carouselImagePath ?? '', {
		width: 359,
		fit: 'crop',
		dpr: 2
	});

	// Track Invoice Viewed event once per page load
	useEffect(() => {
		if (!hasTrackedInvoiceView.current && invoice) {
			const addOns = checkoutState.addOnItemsDTO || [];
			const addOnCount = addOns.reduce((total, item) => total + (item.quantity || 0), 0);

			const addOnTotalPrice = (invoice?.lineItems || [])
				.filter(li => li.item?.type === TDR.Reservation.ItemType.AddOn)
				.reduce((sum, li) => sum + ((li.item?.price || 0) * (li.item?.quantity || 0)), 0);

			analytics.track('Invoice Viewed', {
				restaurantId: restaurant.id,
				restaurantName: restaurant.name,
				tableId: checkoutSession.tableId,
				checkoutId: checkoutSession.id,
				totalAmount: invoice?.total || 0,
				subtotalAmount: invoice?.subtotal || 0,
				taxAmount: invoice?.subtotalPriceAdjustments?.reduce((sum, item) => sum + (item.total || 0), 0) || 0,
				depositAmount: invoice?.deposit || 0,
				addOnCount,
				addOnTotalPrice,
				hasAddOns: addOnCount > 0,
				currency: invoice?.currency
			});

			hasTrackedInvoiceView.current = true;
		}
	}, [invoice, checkoutSession, restaurant, checkoutState.addOnItemsDTO]);

	useEffect(() => {
		if (!!checkoutState.stripePaymentMethod) {
			setIsPaymentInfoComplete(true);
		}
		else {
			setIsPaymentInfoComplete(false);
		}
	}, [checkoutState.stripePaymentMethod]);

	const handleError = (error: StripeError) => {
		try {
			if (!error) {
				setStripeError('');
				return;
			}
			if (error.type === 'card_error' || error.type === 'validation_error' || error.type === 'invalid_request_error') {
				setStripeError(error.message);
				analytics.track('Payment Input User Error', { reservationId: checkoutSession.id });
			}
			else {
				throw error;
			}
		}
		catch (e) {
			setStripeError('An unexpected error occurred, update your payment details and try again, later.');
			const eventId = Sentry.captureException(e);
			trackReservationCreation(checkoutState, checkoutSession, invoice, e.message, eventId);
		}
	};

	const handleFreeBooking = async () => {
		const result = await confirmFreeCheckoutSession(checkoutState.guestDetailsDTO);
		if (result.success) {
			trackReservationCreation(checkoutState, checkoutSession, invoice, null, null);
			navigate({ pathname: `/${restaurant.slug}/checkout/${checkoutSession.id}/confirmation` }, { replace: true });
		}
		else {
			setFreeBookingError(true);
		}
	};

	const handleSubmit = async (prefilledPayment: boolean) => {
		setIsSubmitting(true);
		try {
			// update fast checkout setting for guest
			if (isSaveContactChecked) {
				enableFastCheckout({ checkoutId: checkoutSession.id });
			}

			// Handles 100% promo code discounts
			if (!requiresCreditCard && invoice.total === 0) {
				await handleFreeBooking();
				return;
			}

			let stripeResponse: PaymentIntentResult | SetupIntentResult;

			const stripeConfirmation = invoice.total > 0 ? stripe.confirmPayment : stripe.confirmSetup;

			if (prefilledPayment) {
				stripeResponse = await stripeConfirmation({
					clientSecret: checkoutState.stripeClientSecret,
					redirect: 'if_required'
				});
			}
			else {
				stripeResponse = await stripeConfirmation({
					elements,
					redirect: 'if_required',
					confirmParams: {
						payment_method_data: {
							billing_details: {
								email: checkoutState.guest.email,
								name: checkoutState.guest.firstName + ' ' + checkoutState.guest.lastName,
								phone: checkoutState.guest.phone
							}
						}
					}
				});
			}

			const error = (stripeResponse as PaymentIntentResult)?.error;

			if (error) {
				handleError(error);
			}
			else {
				trackReservationCreation(checkoutState, checkoutSession, invoice);

				analytics.track(CONFIRM_RESERVATION, {
					restaurantId: restaurant.id,
					restaurantName: restaurant.name,
					tableId: checkoutSession.tableId,
					tableName: displayTableName || 'Unknown',
					guests: checkoutSession.guests.toString(),
					checkoutId: checkoutSession.id,
					totalAmount: (invoice?.total || 0).toString(),
					depositAmount: (invoice?.deposit || 0).toString(),
					currency: invoice?.currency || 'CAD',
					hasAddOns: (checkoutState.addOnItemsDTO?.length > 0).toString(),
					addOnCount: (checkoutState.addOnItemsDTO?.length || 0).toString()
				});

				// Track add_payment_info event for GTM
				gtm.addPaymentInfo({
					checkoutId: checkoutSession.id,
					value: invoice.total,
					guests: checkoutSession.guests,
					date: luxonStartDateTime.toFormat('yyyy-LL-dd'),
					time: luxonStartDateTime.toFormat('HH:mm')
				});

				navigate({ pathname: `/${restaurant.slug}/checkout/${checkoutSession.id}/confirmation` }, { replace: true });
			}
		}
		catch (e) {
			const eventId = Sentry.captureException(e);
			trackReservationCreation(checkoutState, checkoutSession, invoice, e.message, eventId);
			setStripeError('An unexpected error occurred, update your payment details and try again, later.');
		}
		finally {
			setIsSubmitting(false);
		}
	};

	const isScrolledToBottom = () => {
		if (scrollRef.current) {
			const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
			return scrollTop + clientHeight >= scrollHeight;
		}
		else {
			return false;
		}
	};

	// Show scroll down button if user is inactive for 10 seconds
	useEffect(() => {
		let timeoutId: NodeJS.Timeout;

		const userEvents = ['mousemove', 'keydown', 'scroll'];

		const resetTimer = () => {
			clearTimeout(timeoutId);
			timeoutId = setTimeout(() => {
				setIsScrollButtonVisible(true);
			}, 10000);
		};

		resetTimer();
		userEvents.forEach((event) => window.addEventListener(event, resetTimer));

		return () => {
			clearTimeout(timeoutId);
			userEvents.forEach((event) => window.removeEventListener(event, resetTimer));
		};
	}, []);

	if (freeBookingError) {
		return <ConfirmationFailed restaurantSlug={restaurant.slug} sessionId={checkoutSession.id} />;
	}

	return (
		<>
			<div className={`${layoutStyles.Body} ${layoutStyles.BodyNoGap}`} ref={scrollRef}>
				<section className={`${layoutStyles.Section} !mt-5 sm:!mt-0`}>
					{table ? (
						<ImageCard height={240} brightness={restaurant.brightness ?? 1} contrast={restaurant.contrast ?? 1}>
							<ImageCardAsset src={imageSrc} alt={displayTableName || table.name} />
							<ImageCardHeader>
								<ImageCardHeaderPrimary>
									<ImageCardHeaderTitle>
										<span className="font-lora">{displayTableName || table.name}</span>
									</ImageCardHeaderTitle>
								</ImageCardHeaderPrimary>
							</ImageCardHeader>
						</ImageCard>
					) : null}
					<FilterIcons groupSize={checkoutSession.guests} date={displayDate} time={displayTime} />
					{addOns.length ? <AddOnOrderSummary addOns={addOns} currency={restaurant.currency} /> : null}
				</section>

				<section className={layoutStyles.Section}>
					<SubHeader title={t('titles.order_summary')} />
					<PromoCodeInput checkoutSessionId={checkoutSession.id} invoiceId={invoice.id} />
					<OrderSummary />
				</section>

				{requiresCreditCard && (
					<section className={layoutStyles.Section}>
						<SubHeader
							title={t('titles.payment_method')}
							subtitle={
								<>
									{t('payment.secure_encrypted')}
									<SecureEncryptInfo />
								</>
							}
							isRequired
						/>
						<PaymentInput
							stripePaymentMethod={checkoutState.stripePaymentMethod}
							promoCode={checkoutState.promoCode}
							setIsPaymentInfoComplete={setIsPaymentInfoComplete}
							onError={handleError}
						/>
					</section>
				)}

				<section className={layoutStyles.Section}>
					<ContactUsFooter />
				</section>

				<AnimatePresence>
					{isScrollButtonVisbile && !isScrolledToBottom() && (
						<motion.div
							style={{
								position: 'absolute',
								bottom: 72.5,
								width: '100%'
							}}
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							onMouseLeave={() => setIsScrollButtonVisible(false)}
						>
							<ScrollDownButton scrollRef={scrollRef} />
						</motion.div>
					)}
				</AnimatePresence>
			</div>

			<div className={layoutStyles.Footer}>
				{stripeError && <InputValidationError message={stripeError} />}
				{/*
				  * checkoutState.isLargeGroup derives from this same table via the session's items
				  * (CheckoutContext), and that snapshot -- resolved server-side for this booking's own
				  * date and party size -- is authoritative. The table doc's default was a third source
				  * that could only ever disagree with it.
				  */}
				<CTAButton
					buttonText={(checkoutState.isLargeGroup || featureFlags?.privateEvents) ? getCheckoutCTALabel(CheckoutCTALabel.SUBMIT, t) : getCheckoutCTALabel(CheckoutCTALabel.CONFIRM, t)}
					disabled={!isValid || !!isFreeBookingSubmitting || isSubmitting || !!isReleasing}
					loading={!!isFreeBookingSubmitting || isSubmitting || !!isReleasing}
					onClick={() => handleSubmit(!!checkoutState.stripePaymentMethod)}
				/>
			</div>
		</>
	);
};
